import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import WebAppContext from '../../utils/webAppContext';
import dayjs from 'dayjs';
import { gql, useQuery, useSubscription } from '@apollo/client';
import SensorChart from '../../organisms/DeviceCard/SensorDataChart';
import Page from '../../atoms/Page';
import { useAnalysisFilter } from '../../utils/useFeedFrameFilter';
import './LiveFeedFramePage.scss';
import { convertGramsToSmallUnits, weightSmallUnitLabel } from '../../utils/unitConversion';
import PropTypes from 'prop-types';

const LOOKBACK_MINUTES = 2;
const DEVICE_INFO_GQL = gql`
  query DeviceInfoQuery($serial: String) {
    device(where: { serial: { _eq: $serial } }) {
      id
    }
  }
`;

const FEED_FRAME_SUB_GQL = gql`
  subscription LiveFeedFramSubscription($serial: String, $feedFrameAnalysisWhere: feed_frame_analysis_bool_exp!, $after: bigint) {
    feed_frame(
      limit: 1
      order_by: { started_at: desc }
      where: { device: { serial: { _eq: $serial } }, started_at: { _gte: $after } }
    ) {
      id
      started_at
      ended_at
      created_at
      updated_at

      feed_frame_analyses(where: $feedFrameAnalysisWhere) {
        id
        algorithm_version
        created_at
        latest_estimated_mass_moved_in_grams
      }
    }
  }
`;

function LiveFeedFramePage({ hideUIChrome }) {
  const { serial } = useParams();
  const [startTime, setStartTime] = useState(0);
  const { isMetric } = useContext(WebAppContext);
  const feedFrameAnalysisWhere = useAnalysisFilter();

  useEffect(() => {
    hideUIChrome?.(true);
  }, [hideUIChrome]);

  useEffect(() => {
    const newStartTime = dayjs().subtract(LOOKBACK_MINUTES, 'minute').unix();
    setStartTime(newStartTime);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', (event) => {
      if (event?.code === 'Space') {
        const newStartTime = dayjs().subtract(LOOKBACK_MINUTES, 'minute').unix();
        setStartTime(newStartTime);
      }
    });
    // returned function will be called on component unmount
    return () => {
      window.removeEventListener('keydown', () => {});
    };
  }, []);

  const { loading, data: deviceData } = useQuery(DEVICE_INFO_GQL, {
    variables: {
      serial,
    },
  });

  const {
    loading: subscriptionLoading,
    error: subscriptionError,
    data: subscriptionData,
  } = useSubscription(FEED_FRAME_SUB_GQL, {
    variables: {
      serial,
      feedFrameAnalysisWhere,
      after: startTime,
    },
    skip: false,
  });

  if (loading) {
    return <></>;
  }

  if (!deviceData.device[0]) {
    return <>No Device for that serial number</>;
  }

  const buildFeedFrameView = (subscriptionLoading, subscriptionError, state) => {
    if (subscriptionError) {
      return <>Subscription Error</>;
    }

    if (subscriptionLoading) {
      return <>Loading</>;
    }

    return (
      <>
        <p className="status">
          {state.waiting && 'Waiting'}
          {state.running && 'Feed Line Running'}
          {state.processing && 'Analysing Data'}
        </p>
        <p>
          <span className="weightNumber">
            {!state.grams ? '--.-' : convertGramsToSmallUnits(isMetric, state.grams).toFixed(1)}
          </span>
          <span className="weightUnits">{weightSmallUnitLabel(isMetric)}</span>
        </p>
        <p className="duration">{!state.duration || state.duration < 0 ? '--' : state.duration} seconds</p>
      </>
    );
  };

  return (
    <Page className="LiveFeedFramePage">
      <div className="flexParent">
        <div className="chartHolder">
          <SensorChart
            deviceId={deviceData.device[0].id}
            from={dayjs(startTime * 1000)}
            to={dayjs(startTime * 1000).add(5, 'minute')}
            live={true}
            showEvents={false}
            showFeedFrames={false}
            showDebugData={false}
            showTemperature={false}
            showAccelerometer={false}
          />
        </div>
        <div className="feedFrameHolder">
          <img className="logo" src="/images/FeedFlo_text_logo.svg" width={300} />
          <div className="cardHolder">
            {buildFeedFrameView(subscriptionLoading, subscriptionError, {
              waiting: !subscriptionData?.feed_frame?.[0]?.started_at,
              running: subscriptionData?.feed_frame?.[0]?.started_at && !subscriptionData?.feed_frame?.[0]?.ended_at,
              processing:
                subscriptionData?.feed_frame?.[0]?.started_at &&
                subscriptionData?.feed_frame?.[0]?.ended_at &&
                !subscriptionData?.feed_frame?.[0]?.feed_frame_analyses?.[0]?.latest_estimated_mass_moved_in_grams,
              duration: subscriptionData?.feed_frame?.[0]?.ended_at - subscriptionData?.feed_frame?.[0]?.started_at,
              grams: subscriptionData?.feed_frame?.[0]?.feed_frame_analyses?.[0]?.latest_estimated_mass_moved_in_grams,
            })}
          </div>
        </div>
      </div>
    </Page>
  );
}

LiveFeedFramePage.propTypes = {
  hideUIChrome: PropTypes.func,
};

export default LiveFeedFramePage;
